// Mapa y orden de las paradas de un recorrido (Recorridos → Nuevo recorrido
// y el detalle de uno que todavía se arma).
//
//   · «Ordenar recorrido»: ruteo simple (lib/routeOrder) desde el inicio
//     elegido y, si se elige, hasta un fin. Con `streetRouting` ordena por
//     tiempo de manejo real (Valhalla); sin él, o si no responde, en línea
//     recta. Inicio y fin: un depósito, una dirección buscada o un punto
//     tocado en el mapa.
//   · En el mapa: cada parada con su número y el recorrido, por calle cuando
//     hay `streetRouting` (se pide de nuevo solo si cambia el orden). Tocar
//     una la selecciona y abajo aparecen subir, bajar, mover a un número y
//     quitar. «Tocar en orden» renumera tocando las paradas una por una.
//
// No guarda nada: avisa el orden nuevo con onOrder (las `key` de `items`, en
// el orden nuevo) y las bajas con onRemove (la `key`); quien lo usa decide si
// es estado local o la API. Claves y no índices: ordenar por calles espera la
// matriz unos segundos y la lista sigue editable; si mientras tanto cambió
// qué paradas hay, ese orden es de otra lista y no se manda (se pide ordenar
// de nuevo). Con validateOrder, un orden que no vale (un Turbo con la entrega
// antes del retiro) ni se manda: se explica abajo del mapa.
//
// Los labels vienen de direcciones, tiendas y del buscador: en el mapa van
// siempre como texto (textTooltip), nunca como HTML.

import React, { useEffect, useRef, useState } from "react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { panelStyle, smallMutedStyle } from "../ui/pageStyles";
import { useLeaflet } from "../../hooks/useLeaflet";
import { createBaseMap, numberIcon as boxNumberIcon } from "../../lib/baseMap";
import { driverName } from "../../lib/driverName";
import type { MapHome } from "../../lib/mapLayers";
import { CostFn, hasCoords, moveTo, pathKm, planOrder, Pt } from "../../lib/routeOrder";
import { textTooltip } from "../../lib/leafletText";
import { decodePolyline, fmtDuration, fmtKm, STREET_MAX_POINTS, StreetRouting } from "../../lib/streetRouting";

export type PlannerItem = { key: string; label: string; lat?: number | null; lng?: number | null; kind?: "PICKUP" | "DELIVERY" };
export type PlannerWarehouse = { id: number; name: string; type: string; latitude: number | null; longitude: number | null };
// Domicilio de un chofer (GET /v2/driver-home): se ofrece como inicio o fin.
export type PlannerHome = { driverId: number; name: string; address: string; lat: number; lng: number };
export const toPlannerHome = (h: MapHome): PlannerHome => ({ driverId: h.driver_id, name: driverName(h), address: h.address, lat: h.latitude, lng: h.longitude });

type Place = { pt: Pt; label: string };
type Mode = null | "start" | "end" | "sequence";
// Trazado por calle de un orden dado (key = los puntos en ese orden).
type Street = { key: string; lines: [number, number][][]; km: number; seconds: number } | { key: string; error: string };
const STREET_CACHE = 20;

// Centro de CABA: el mapa se encuadra solo apenas hay una parada o un inicio.
const CENTRO: [number, number] = [-34.6037, -58.3816];
const selectStyle: React.CSSProperties = { width: "100%", minHeight: "40px", padding: "0 12px", borderRadius: "12px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)" };

const BLUE = "#2f7cf6", ORANGE = "#f0883e", GREEN = "#2ea043", RED = "#da3633", GREY = "#8b949e";
const numberIcon = (L: any, text: string, color: string, big = false) => boxNumberIcon(L, text, color, { size: big ? 30 : 24 });

// Largo y tiempo de un camino según la matriz por calles. Un tramo sin camino
// (null en la matriz) no tiene número que sumar: se cuenta aparte y se avisa,
// en vez de sumarlo como 0 (el orden ya lo evita: le pone un costo enorme).
export function measureStreetPath(path: (number | string)[], cell: (t: (number | null)[][], a: number | string, b: number | string) => number | null, m: { seconds: (number | null)[][]; km: (number | null)[][] }) {
  let s = 0, km = 0, unreachable = 0;
  for (let k = 1; k < path.length; k++) {
    const sec = cell(m.seconds, path[k - 1], path[k]), dist = cell(m.km, path[k - 1], path[k]);
    if (sec == null || dist == null) { unreachable += 1; continue; }
    s += sec; km += dist;
  }
  return { s, km, unreachable };
}
const sinCamino = (n: number) => (n ? `, sin contar ${n} tramo${n === 1 ? "" : "s"} sin camino` : "");

// Reordena `list` según `keys` (lo que manda onOrder). null si no son
// exactamente las mismas paradas: un orden de otra lista no se aplica.
export function orderByKeys<T>(list: T[], keys: string[], keyOf: (x: T) => string): T[] | null {
  const byKey = new Map(list.map((x) => [keyOf(x), x]));
  if (byKey.size !== list.length || keys.length !== list.length || new Set(keys).size !== keys.length) return null;
  const out: T[] = [];
  for (const k of keys) { const x = byKey.get(k); if (x === undefined) return null; out.push(x); }
  return out;
}
const sameKeys = (a: PlannerItem[], b: PlannerItem[]) => {
  if (a.length !== b.length) return false;
  const ka = new Set(a.map((it) => it.key));
  return b.every((it) => ka.has(it.key));
};
export const STALE_ORDER_MSG = "La lista de paradas cambió mientras se ordenaba: ordená de nuevo.";

const whPoint = (w: PlannerWarehouse | undefined): Place | null =>
  w && hasCoords({ lat: w.latitude ?? NaN, lng: w.longitude ?? NaN }) ? { pt: { lat: w.latitude as number, lng: w.longitude as number }, label: w.name } : null;

export default function RoutePlanner({
  items, warehouses, defaultWarehouseId = null, defaultEnd = "free", editable, canOptimize = true, optimizeHint, busy = false,
  onOrder, onRemove, searchAddress, validateOrder, streetRouting, homes = [], assignedDriverId = null,
}: {
  items: PlannerItem[];
  warehouses: PlannerWarehouse[];
  defaultWarehouseId?: number | null;
  // Colecta: vuelve al depósito; reparto: termina en la última entrega.
  defaultEnd?: "free" | "start";
  editable: boolean;
  canOptimize?: boolean;
  optimizeHint?: string;
  busy?: boolean;
  onOrder: (keys: string[]) => void | Promise<void>;
  onRemove?: (key: string) => void | Promise<void>;
  // Geocodifica un texto (GET /v2/deliveries/geo/search); sin esto, solo se
  // puede marcar el punto en el mapa.
  searchAddress?: (q: string) => Promise<Place | null>;
  // null si el orden vale; si no, el motivo (no se llama a onOrder).
  validateOrder?: (keys: string[]) => string | null;
  // Ruteo por calles (POST /v2/routes/street/*); sin esto, línea recta.
  streetRouting?: StreetRouting;
  // Domicilios de los choferes, como opción de inicio o fin (solo se
  // ofrecen, nunca se eligen solos). El del chofer asignado va primero.
  homes?: PlannerHome[];
  assignedDriverId?: number | null;
}) {
  const leaflet = useLeaflet();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const fittedFor = useRef<string>("");

  const located = warehouses.filter((w) => whPoint(w));
  // De entrada, el depósito del recorrido (o un Crossdock); nunca un Dropoff
  // cualquiera. Si el del recorrido no tiene coordenadas, se avisa.
  const firstWh = located.find((w) => w.id === defaultWarehouseId) ?? (defaultWarehouseId ? undefined : located.find((w) => w.type === "CROSSDOCK"));
  const defaultSinUbicar = defaultWarehouseId && !located.some((w) => w.id === defaultWarehouseId)
    ? warehouses.find((w) => w.id === defaultWarehouseId)?.name ?? null : null;
  const [startSel, setStartSel] = useState<string>(firstWh ? `wh:${firstWh.id}` : "none");
  const [endSel, setEndSel] = useState<string>(defaultEnd === "start" && firstWh ? "same" : "free");
  const [startPlace, setStartPlace] = useState<Place | null>(null);
  const [endPlace, setEndPlace] = useState<Place | null>(null);
  const [query, setQuery] = useState<{ which: "start" | "end"; text: string } | null>(null);
  const [mode, setMode] = useState<Mode>(null);
  // «Tocar en orden»: claves de las paradas (no índices: si `items` se
  // recarga o cambia de orden, un índice pasa a señalar otra parada).
  const [picked, setPicked] = useState<string[]>([]);
  // La parada elegida, también por clave: sigue a la parada si se reordena.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [moveText, setMoveText] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const resolve = (sel: string, place: Place | null): Place | null => {
    if (sel.startsWith("wh:")) return whPoint(warehouses.find((w) => w.id === Number(sel.slice(3))));
    if (sel === "addr") return place;
    if (sel.startsWith("home:")) {
      const h = homes.find((x) => x.driverId === Number(sel.slice(5)));
      return h ? { pt: { lat: h.lat, lng: h.lng }, label: `Domicilio de ${h.name}` } : null;
    }
    return null;
  };
  const start = resolve(startSel, startPlace);
  const end = endSel === "same" ? start : resolve(endSel, endPlace);

  const coords = items.map((it) => (hasCoords({ lat: it.lat ?? NaN, lng: it.lng ?? NaN }) ? { lat: it.lat as number, lng: it.lng as number } : null));
  const missingCount = coords.filter((c) => !c).length;
  const kmNow = pathKm(coords.filter((c): c is Pt => !!c), start?.pt ?? null, end?.pt ?? null);

  // Recorrido por calle del orden actual. Se pide un rato después del último
  // cambio y se guarda por orden: volver a uno ya visto no consulta de nuevo.
  const routePts: Pt[] = [...(start ? [start.pt] : []), ...coords.filter((c): c is Pt => !!c), ...(end ? [end.pt] : [])];
  const streetKey = routePts.map((p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`).join(";");
  const streetUsable = !!streetRouting && routePts.length >= 2 && routePts.length <= STREET_MAX_POINTS;
  const [street, setStreet] = useState<Street | null>(null);
  const streetCache = useRef(new Map<string, Street>());
  useEffect(() => {
    if (!streetUsable || !streetRouting) return;
    const cached = streetCache.current.get(streetKey);
    if (cached) { setStreet(cached); return; }
    let cancelled = false;
    const pts = routePts;
    const timer = window.setTimeout(async () => {
      let value: Street;
      try {
        const r = await streetRouting.shape(pts);
        value = { key: streetKey, lines: r.shapes.map((sh) => decodePolyline(sh)), km: r.km, seconds: r.seconds };
      } catch (e: any) { value = { key: streetKey, error: e.message }; }
      if (!("error" in value)) {
        streetCache.current.set(streetKey, value);
        if (streetCache.current.size > STREET_CACHE) streetCache.current.delete(streetCache.current.keys().next().value as string);
      }
      if (!cancelled) setStreet(value);
    }, 400);
    return () => { cancelled = true; window.clearTimeout(timer); };
    // routePts cambia de identidad en cada render; streetKey resume su contenido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streetRouting, streetUsable, streetKey]);
  const streetNow = streetUsable && street && street.key === streetKey ? street : null;
  const streetOk = streetNow && !("error" in streetNow) ? streetNow : null;

  // Lo que el clic del mapa (y un orden que termina después de un await)
  // necesita leer al momento: la lista y los callbacks del último render.
  const live = useRef({ mode, picked, items, onOrder, validateOrder });
  live.current = { mode, picked, items, onOrder, validateOrder };

  const found = selectedKey === null ? -1 : items.findIndex((it) => it.key === selectedKey);
  const selected = found >= 0 ? found : null;
  useEffect(() => { if (selectedKey !== null && !items.some((it) => it.key === selectedKey)) setSelectedKey(null); }, [items, selectedKey]);
  // Una parada que ya no está (se quitó) deja de contar como tocada.
  useEffect(() => {
    setPicked((p) => { const keys = new Set(items.map((it) => it.key)); const n = p.filter((k) => keys.has(k)); return n.length === p.length ? p : n; });
  }, [items]);

  // El mapa se crea una sola vez.
  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = (window as any).L;
    // Zoom de a medio nivel y la ruedita más suave, igual que Choferes en vivo
    // (lib/baseMap): de a un nivel entero el salto era demasiado grande.
    const map: any = createBaseMap(L, containerRef.current, { center: CENTRO, zoom: 11, maxZoom: 19 });
    layerRef.current = L.layerGroup().addTo(map);
    map.on("click", (e: any) => {
      const m = live.current.mode;
      if (m !== "start" && m !== "end") return;
      const place = { pt: { lat: e.latlng.lat, lng: e.latlng.lng }, label: "punto marcado en el mapa" };
      if (m === "start") { setStartPlace(place); setStartSel("addr"); } else { setEndPlace(place); setEndSel("addr"); }
      setMode(null);
    });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [leaflet]);

  // Paradas, inicio, fin y la línea del recorrido.
  const keysSig = items.map((it) => it.key).sort().join(",");
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const L = (window as any).L;
    const g = layerRef.current;
    g.clearLayers();
    const line: [number, number][] = [];
    if (start) line.push([start.pt.lat, start.pt.lng]);
    coords.forEach((c) => { if (c) line.push([c.lat, c.lng]); });
    if (end) line.push([end.pt.lat, end.pt.lng]);
    if (streetOk) streetOk.lines.forEach((l) => L.polyline(l, { color: BLUE, weight: 4, opacity: 0.8 }).addTo(g));
    // Línea recta: sin ruteo por calles, o mientras llega (punteada).
    else if (line.length > 1) L.polyline(line, { color: BLUE, weight: 3, opacity: 0.7, ...(streetUsable ? { dashArray: "6 6" } : {}) }).addTo(g);

    const sameEnd = !!(start && end && start.pt.lat === end.pt.lat && start.pt.lng === end.pt.lng);
    if (start) L.marker([start.pt.lat, start.pt.lng], { icon: numberIcon(L, sameEnd ? "I/F" : "I", GREEN, true), zIndexOffset: 500 }).bindTooltip(textTooltip(`Inicio · ${start.label}`)).addTo(g);
    if (end && !sameEnd) L.marker([end.pt.lat, end.pt.lng], { icon: numberIcon(L, "F", RED, true), zIndexOffset: 500 }).bindTooltip(textTooltip(`Fin · ${end.label}`)).addTo(g);

    coords.forEach((c, i) => {
      if (!c) return;
      const key = items[i].key;
      const order = picked.indexOf(key);
      const color = mode === "sequence" ? (order >= 0 ? ORANGE : GREY) : i === selected ? ORANGE : BLUE;
      const text = mode === "sequence" && order >= 0 ? String(order + 1) : String(i + 1);
      const m = L.marker([c.lat, c.lng], { icon: numberIcon(L, text, color), zIndexOffset: i === selected ? 1000 : 0 })
        .bindTooltip(textTooltip(`${i + 1}. ${items[i].label}`));
      m.on("click", () => {
        const cur = live.current;
        if (cur.mode === "sequence") {
          if (!cur.picked.includes(key)) setPicked([...cur.picked, key]);
          return;
        }
        setSelectedKey(key); setMoveText(String(i + 1));
      });
      m.addTo(g);
    });

    // Encuadre: al abrir y cada vez que cambia QUÉ paradas hay (no el orden).
    const sig = `${keysSig}|${start?.label ?? ""}|${end?.label ?? ""}`;
    if (fittedFor.current !== sig && line.length) {
      map.fitBounds(L.latLngBounds(line), { padding: [28, 28], maxZoom: 15 });
      fittedFor.current = sig;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaflet, items, start?.pt.lat, start?.pt.lng, end?.pt.lat, end?.pt.lng, selected, mode, picked, streetOk, streetUsable]);

  // true si el orden se guardó. `order`: índices de `base`, la lista sobre la
  // que se calculó; si ya no son las mismas paradas, no se manda.
  const apply = async (base: PlannerItem[], order: number[], note?: string): Promise<boolean> => {
    const cur = live.current;
    if (!sameKeys(base, cur.items)) { setMsg(STALE_ORDER_MSG); return false; }
    const keys = order.map((i) => base[i].key);
    const invalid = cur.validateOrder?.(keys) ?? null;
    if (invalid) { setMsg(invalid); return false; }
    setWorking(true); setMsg(null);
    try { await cur.onOrder(keys); if (note) setMsg(note); return true; } catch (e: any) { setMsg(e.message); return false; } finally { setWorking(false); }
  };

  const optimize = async () => {
    const sinUbicar = (plan: { missing: number[] }) => (plan.missing.length ? ` ${plan.missing.length} parada(s) sin ubicación quedaron al final.` : "");
    let aviso = "";
    if (streetUsable && streetRouting) {
      // Matriz por calle entre paradas ubicadas, inicio y fin (si el fin es el
      // inicio, es el mismo punto); se ordena por tiempo de manejo.
      const located = coords.map((c, i) => (c ? i : -1)).filter((i) => i >= 0);
      const points: Pt[] = located.map((i) => coords[i] as Pt);
      const nodeOf = new Map<number | string, number>(located.map((i, k) => [i, k]));
      if (start) { nodeOf.set("start", points.length); points.push(start.pt); }
      const sameEnd = !!(start && end && start.pt.lat === end.pt.lat && start.pt.lng === end.pt.lng);
      if (end) { if (sameEnd) nodeOf.set("end", nodeOf.get("start") as number); else { nodeOf.set("end", points.length); points.push(end.pt); } }
      setWorking(true); setMsg("Ordenando por calles…");
      try {
        const m = await streetRouting.matrix(points);
        // Sin camino entre dos puntos: tan caro que el orden lo evita.
        const cell = (t: (number | null)[][], a: number | string, b: number | string) => t[nodeOf.get(a) as number]?.[nodeOf.get(b) as number] ?? null;
        const cost: CostFn = (a, b) => cell(m.seconds, a, b) ?? 1e7;
        const plan = planOrder(coords, start?.pt ?? null, end?.pt ?? null, cost);
        const measure = (order: number[]) =>
          measureStreetPath([...(start ? ["start"] : []), ...order.filter((i) => coords[i]), ...(end ? ["end"] : [])], cell, m);
        const before = measure(items.map((_, i) => i)), after = measure(plan.order);
        setWorking(false);
        await apply(items, plan.order, `Ordenado por calles: ${fmtKm(after.km)} · ${fmtDuration(after.s)} de manejo${sinCamino(after.unreachable)} (antes ${fmtKm(before.km)} · ${fmtDuration(before.s)}${sinCamino(before.unreachable)}).${sinUbicar(plan)}`);
        return;
      } catch (e: any) {
        aviso = `No se pudo ordenar por calles (${e.message}): se ordenó en línea recta. `;
      } finally { setWorking(false); }
    }
    const plan = planOrder(coords, start?.pt ?? null, end?.pt ?? null);
    apply(items, plan.order, `${aviso}Ordenado: ${fmtKm(kmNow)} → ${fmtKm(plan.km)} en línea recta.${sinUbicar(plan)}`);
  };

  // La selección sigue a la parada (por clave); el número, solo si el orden
  // nuevo se guardó.
  const moveSelected = async (to: number) => {
    if (selected === null) return;
    const idx = items.map((_, i) => i);
    const target = Math.max(0, Math.min(items.length - 1, to));
    if (await apply(items, moveTo(idx, selected, target))) setMoveText(String(target + 1));
  };

  // «Tocar en orden»: lo tocado primero, en ese orden; lo demás, después, como estaba.
  const applySequence = () => {
    const pos = new Map(items.map((it, i) => [it.key, i]));
    const first = picked.map((k) => pos.get(k)).filter((i): i is number => i !== undefined);
    const rest = items.map((_, i) => i).filter((i) => !first.includes(i));
    apply(items, [...first, ...rest], `Renumeradas ${first.length} parada(s) en el orden en que se tocaron.`);
    setPicked([]); setMode(null);
  };
  useEffect(() => {
    if (mode === "sequence" && picked.length && picked.length === coords.filter(Boolean).length) applySequence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);

  const doSearch = async () => {
    if (!query || !searchAddress || !query.text.trim()) return;
    setWorking(true); setMsg(null);
    try {
      const found = await searchAddress(query.text.trim());
      if (!found) { setMsg("Sin resultados para esa dirección: probá marcarla en el mapa."); return; }
      if (query.which === "start") { setStartPlace(found); setStartSel("addr"); } else { setEndPlace(found); setEndSel("addr"); }
      setQuery(null);
    } catch (e: any) { setMsg(`${e.message}. Probá marcarla en el mapa.`); } finally { setWorking(false); }
  };

  const assignedHome = assignedDriverId ? homes.find((h) => h.driverId === assignedDriverId) ?? null : null;
  const otherHomes = homes.filter((h) => h !== assignedHome);
  const placeOptions = (which: "start" | "end") => (
    <>
      {located.map((w) => <option key={w.id} value={`wh:${w.id}`}>{w.name}</option>)}
      {assignedHome ? <option value={`home:${assignedHome.driverId}`}>Domicilio del chofer ({assignedHome.name})</option> : null}
      {otherHomes.length ? (
        <optgroup label="Domicilio de otro chofer">
          {otherHomes.map((h) => <option key={h.driverId} value={`home:${h.driverId}`}>{h.name} · {h.address}</option>)}
        </optgroup>
      ) : null}
      <option value="addr">{(which === "start" ? startPlace : endPlace)?.label ? `Dirección: ${(which === "start" ? startPlace : endPlace)!.label}` : "Una dirección…"}</option>
    </>
  );
  const onPlaceChange = (which: "start" | "end", v: string) => {
    if (which === "start") setStartSel(v); else setEndSel(v);
    if (v === "addr" && !(which === "start" ? startPlace : endPlace)) setQuery({ which, text: "" });
  };

  const disabled = busy || working;
  const sel = selected !== null ? items[selected] : null;

  return (
    <div style={panelStyle} data-testid="route-planner">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px", alignItems: "end" }}>
        <label style={{ display: "block" }}>
          <span style={{ ...smallMutedStyle, display: "block", marginBottom: "6px", fontWeight: 600 }}>Inicio</span>
          <select aria-label="Inicio" style={selectStyle} value={startSel} onChange={(e) => onPlaceChange("start", e.target.value)} disabled={disabled}>
            <option value="none">Sin inicio fijo</option>
            {placeOptions("start")}
          </select>
        </label>
        <label style={{ display: "block" }}>
          <span style={{ ...smallMutedStyle, display: "block", marginBottom: "6px", fontWeight: 600 }}>Fin</span>
          <select aria-label="Fin" style={selectStyle} value={endSel} onChange={(e) => onPlaceChange("end", e.target.value)} disabled={disabled}>
            <option value="free">Donde termine (última parada)</option>
            <option value="same" disabled={!start}>Volver al inicio</option>
            {placeOptions("end")}
          </select>
        </label>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {editable && canOptimize ? <Button onClick={optimize} disabled={disabled || items.length < 2}>Ordenar recorrido</Button> : null}
        </div>
      </div>

      {query ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "8px", alignItems: "end", marginTop: "10px" }}>
          <Input label={query.which === "start" ? "Dirección de inicio" : "Dirección de fin"} value={query.text} placeholder="Calle y número, localidad"
            onChange={(e) => setQuery({ ...query, text: e.target.value })} onKeyDown={(e: any) => { if (e.key === "Enter") doSearch(); }} />
          {searchAddress ? <Button variant="secondary" onClick={doSearch} disabled={disabled || !query.text.trim()}>Buscar</Button> : null}
          <Button variant="secondary" onClick={() => { setMode(query.which); setQuery(null); }} disabled={disabled || !leaflet}>Marcar en el mapa</Button>
          <Button variant="ghost" onClick={() => { setQuery(null); if (query.which === "start" && !startPlace) setStartSel(firstWh ? `wh:${firstWh.id}` : "none"); if (query.which === "end" && !endPlace) setEndSel("free"); }}>Cancelar</Button>
        </div>
      ) : null}
      {(startSel === "addr" || endSel === "addr") && !query ? (
        <div style={{ ...smallMutedStyle, marginTop: "6px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {startSel === "addr" ? <button type="button" onClick={() => setQuery({ which: "start", text: "" })} style={linkStyle}>Cambiar la dirección de inicio</button> : null}
          {endSel === "addr" ? <button type="button" onClick={() => setQuery({ which: "end", text: "" })} style={linkStyle}>Cambiar la dirección de fin</button> : null}
        </div>
      ) : null}

      {defaultSinUbicar && startSel === "none" ? (
        <div style={{ ...smallMutedStyle, marginTop: "8px", color: "var(--warning, #d29922)" }}>
          «{defaultSinUbicar}» no tiene coordenadas (Sistema → Warehouses → Geolocalizar): elegí otro inicio o marcalo en el mapa.
        </div>
      ) : null}
      <div style={{ ...smallMutedStyle, marginTop: "10px" }}>
        {items.length} parada(s) · {streetOk ? `${fmtKm(streetOk.km)} · ${fmtDuration(streetOk.seconds)} de manejo por calles` : `${fmtKm(kmNow)} en línea recta`}
        {streetUsable && !streetNow ? " · calculando el recorrido por calles…" : null}
        {streetNow && "error" in streetNow ? <span style={{ color: "var(--warning, #d29922)" }}> · sin recorrido por calles ({streetNow.error})</span> : null}
        {streetRouting && routePts.length > STREET_MAX_POINTS ? <span style={{ color: "var(--warning, #d29922)" }}> · más de {STREET_MAX_POINTS - 2} paradas: el ruteo por calles no las toma, va en línea recta</span> : null}
        {missingCount ? <span style={{ color: "var(--warning, #d29922)" }}> · {missingCount} sin ubicación (no están en el mapa)</span> : null}
        {!canOptimize && optimizeHint ? ` · ${optimizeHint}` : ""}
      </div>
      {mode === "start" || mode === "end" ? <div style={{ ...smallMutedStyle, marginTop: "6px", color: "var(--primary, #4f8cff)" }}>Tocá el mapa donde {mode === "start" ? "arranca" : "termina"} el recorrido.</div> : null}

      <div ref={containerRef} style={{ height: "380px", marginTop: "10px", borderRadius: "12px", overflow: "hidden", border: "1px solid var(--border)", cursor: mode === "start" || mode === "end" ? "crosshair" : undefined }}>
        {!leaflet ? <div style={{ ...smallMutedStyle, padding: "12px" }}>Cargando mapa…</div> : null}
      </div>

      {editable ? (
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginTop: "10px" }}>
          {mode === "sequence" ? (
            <>
              <span style={smallMutedStyle}>Tocá las paradas en el orden en que tienen que ir · {picked.length} de {coords.filter(Boolean).length}</span>
              <Button variant="secondary" onClick={applySequence} disabled={disabled || picked.length === 0}>Aplicar</Button>
              <Button variant="ghost" onClick={() => setPicked((p) => p.slice(0, -1))} disabled={picked.length === 0}>Deshacer la última</Button>
              <Button variant="ghost" onClick={() => { setPicked([]); setMode(null); }}>Cancelar</Button>
            </>
          ) : sel && selected !== null ? (
            <>
              <span style={{ fontSize: "13px", color: "var(--text-primary)", flex: "1 1 220px" }}><b>{selected + 1}.</b> {sel.label}</span>
              <Button variant="ghost" onClick={() => moveSelected(selected - 1)} disabled={disabled || selected === 0}>↑</Button>
              <Button variant="ghost" onClick={() => moveSelected(selected + 1)} disabled={disabled || selected === items.length - 1}>↓</Button>
              <span style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <span style={smallMutedStyle}>Mover al</span>
                <input aria-label="Mover a la posición" type="number" min={1} max={items.length} value={moveText} onChange={(e) => setMoveText(e.target.value)}
                  style={{ ...selectStyle, width: "70px", minHeight: "34px" }} />
                <Button variant="secondary" onClick={() => moveSelected(Number(moveText) - 1)} disabled={disabled || !Number(moveText)}>Mover</Button>
              </span>
              {onRemove ? <Button variant="ghost" onClick={async () => { const k = sel.key; setSelectedKey(null); setWorking(true); try { await onRemove(k); } catch (e: any) { setMsg(e.message); } finally { setWorking(false); } }} disabled={disabled}>Quitar</Button> : null}
              <Button variant="ghost" onClick={() => setSelectedKey(null)}>Listo</Button>
            </>
          ) : (
            <>
              <span style={smallMutedStyle}>Tocá una parada del mapa para moverla o quitarla.</span>
              <Button variant="secondary" onClick={() => { setSelectedKey(null); setPicked([]); setMode("sequence"); }} disabled={disabled || coords.filter(Boolean).length < 2}>Tocar en orden</Button>
            </>
          )}
        </div>
      ) : null}
      {msg ? <div style={{ ...smallMutedStyle, marginTop: "8px", color: "var(--text-primary)" }}>{msg}</div> : null}
    </div>
  );
}

const linkStyle: React.CSSProperties = { background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--primary, #4f8cff)", font: "inherit" };
